<!--
  抽奖码弹窗（点 banner 下方「抽奖码」按钮弹出）
  头部是通栏头图 + 标题 + 机会数量，左上角有装饰图溢出；
  列表每行是编号 / 任务 + 时间 / 状态，状态分开奖前后两种情形
-->
<script lang="ts">
  import { _ as t } from "svelte-i18n";
  import BottomSheet from "./BottomSheet.svelte";
  import headerBg from "../assets/images/dialog/awardchance-title.png";
  import leftDeco from "../assets/images/dialog/awardchance-left.png";
  import coinIcon from "../assets/images/dialog/awardchance-tcorn.png";
  import happyIcon from "../assets/images/dialog/happy.png";
  import emptyIcon from "../assets/images/dialog/empty.png";

  import type { UserTicketDetail } from "../lib/api";

  interface Props {
    show: boolean;
    onClose: () => void;
    /** 抽奖机会数量（poolInfo.myTicketCount） */
    count?: number;
    /** 我的签池（poolInfo.userTicketDetail） */
    tickets?: UserTicketDetail[];
    /** 活动状态 0-未开始 1-进行中 2-待开奖 3-已完成，用来区分「待开始 / 待开奖」 */
    activityStatus?: number;
  }

  let {
    show,
    onClose,
    count = 0,
    tickets = [],
    activityStatus = 0,
  }: Props = $props();

  // 已作废的签不展示
  let validTickets = $derived(tickets.filter((it) => it.status !== 0));

  /** 默认只展示前 3 条，超出折叠 */
  const MAX_VISIBLE = 3;
  let expanded = $state(false);

  // 弹窗每次重新打开时重置折叠态，避免上次展开后再打开仍停留在展开态
  $effect(() => {
    if (show) expanded = false;
  });

  let visibleTickets = $derived(
    expanded ? validTickets : validTickets.slice(0, MAX_VISIBLE),
  );
  let hasMore = $derived(validTickets.length > MAX_VISIBLE);

  /** 有效签：活动进入待开奖/已完成后显示「待开奖」，否则「待开始」 */
  function pendingKey(): string {
    return activityStatus >= 2
      ? "code_status_pending"
      : "code_status_not_started";
  }

  /** 接口返回 ISO 时间，展示成 2026/08/08 08:08 */
  function formatTime(dateStr?: string): string {
    if (!dateStr) return "";
    const d = new Date(dateStr.replace(/-/g, "/"));
    if (isNaN(d.getTime())) return dateStr;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
</script>

<BottomSheet {show} {onClose} showClose={false} padded={false}>
  <!--
    整个卡片（头图 + 正文）包一层 flex 列，用 max-height 把总高度封顶在
    屏幕 80%：内容没那么多时跟以前一样按内容撑高，超过 80vh 才会封顶。
    封顶后靠下面这条 flex:1 + min-height:0 的链路（lc-body → lc-card →
    lc-rows）把"多出来的高度"逐层让给 lc-rows 去内部滚动，
    头部装饰图、查看更多、确认按钮都是 flex-shrink:0，始终完整可见。
  -->
  <div class="lc-sheet-inner">
    <!-- 头部：通栏头图 + 标题 + 数量 -->
    <div class="lc-header">
      <img src={headerBg} alt="" class="lc-header-bg" />
      <img src={leftDeco} alt="" class="lc-header-deco" />
      <p class="lc-title">{$t("code_dialog_title")}</p>
      <p class="lc-count">
        <span>{count}</span>
        <img src={coinIcon} alt="" class="lc-coin" />
      </p>
    </div>

    <div class="lc-body">
      <!-- 抽奖码列表 -->
      <div class="lc-card">
        <!-- 只让这一块内部滚动；父链路都是 flex:1 + min-height:0，撑不下时空间都让给它 -->
        <div class="lc-rows">
          {#if validTickets.length === 0}
            <!-- 空态：跟 lottery / gold-transfer 一致 -->
            <div class="lc-empty">
              <img src={emptyIcon} alt="" class="lc-empty-icon" />
              <span class="lc-empty-text">{$t("code_empty")}</span>
            </div>
          {/if}

          {#each visibleTickets as ticket, i (ticket.ticketNumber ?? ticket.traceId ?? i)}
            <div class="lc-row">
              <div class="lc-row-main">
                <p class="lc-no">
                  {$t("code_no", {
                    values: { no: ticket.ticketNumber ?? ticket.traceId ?? "" },
                  })}
                </p>
                <p class="lc-meta">
                  {ticket.task ?? ""}
                  {formatTime(ticket.createTime)}
                </p>
              </div>

              <div class="lc-row-status">
                {#if ticket.status === 2}
                  <span class="lc-tag is-won">
                    <img src={happyIcon} alt="" class="lc-tag-icon" />
                    {$t("code_status_won")}
                  </span>
                  {#if ticket.levelName}
                    <span class="lc-tag is-level">{ticket.levelName}</span>
                  {/if}
                {:else if ticket.status === 3}
                  <span class="lc-tag is-lost">{$t("code_status_not_won")}</span
                  >
                {:else}
                  <span class="lc-tag is-pending">{$t(pendingKey())}</span>
                {/if}
              </div>
            </div>
          {/each}
        </div>

        {#if hasMore}
          <button
            type="button"
            class="lc-more"
            onclick={() => (expanded = !expanded)}
          >
            {expanded ? $t("view_less") : $t("view_more")}
            <svg
              class="lc-more-arrow"
              class:is-up={expanded}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        {/if}
      </div>

      <button type="button" class="lc-confirm" onclick={onClose}>
        {$t("btn_confirm")}
      </button>
    </div>
  </div>
</BottomSheet>

<style>
  /*
    整体封顶屏幕 80%。BottomSheet 的 .sheet 本身不裁切/不滚动（要留位置给
    头图装饰溢出），封顶和滚动都在这一层往下处理。
    flex column + min-height: 0 是关键：没有 min-height:0，子项的
    "内容自然高度"会撑破父级的 max-height，flex:1 也不会真的收缩。
  */
  .lc-sheet-inner {
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    min-height: 0;
  }

  .lc-header {
    position: relative;
    /* 头图和装饰图定高，不参与压缩，否则内容超长时头部会被挤变形 */
    flex-shrink: 0;
  }

  /* 头图 750x106@2x = 375x53，通栏，圆角跟弹窗一致 */
  .lc-header-bg {
    display: block;
    width: 100%;
    border-radius: 24px 24px 0 0;
  }

  /* 左上角装饰 252x252@2x = 126x126，向上溢出弹窗顶边 */
  .lc-header-deco {
    position: absolute;
    top: -56px;
    left: -8px;
    width: 126px;
    height: 126px;
  }

  /*
    标题：压在头图上居中
    设计稿：18px / 700 / line-height 20px / #FFFFFF
    / shadow 0 4px 4px #165BFD 60%
  */
  .lc-title {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 53px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0;
    color: #ffffff;
    text-shadow: 0 4px 4px rgba(22, 91, 253, 0.6);
  }

  /*
    数量：贴右
    数字（设计稿）：32px / 600 / line-height 100% / #FFDF00
    / shadow 0 4px 4px #000 15%
  */
  .lc-count {
    position: absolute;
    top: 0;
    right: 16px;
    /* 数字要居中在头图右侧那块深蓝区（图内 y 0~78@2x = 0~39px）里，不是整条 53px */
    height: 36px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 32px;
    font-weight: 600;
    line-height: 100%;
    letter-spacing: 0;
    color: #ffdf00;
    text-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
  }

  .lc-coin {
    width: 23.5px;
    height: 20px;
  }

  /*
    内容区自己从上到下渐变（头图下面重新起一段渐变）。
    flex:1 + min-height:0：把 .lc-sheet-inner 封顶后多出来的（或不够的）
    高度传递下去，撑不满就按内容高度显示，超了就把压缩量交给 .lc-rows。
  */
  .lc-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 12px 16px 16px;
    background: linear-gradient(180deg, #68adff 0%, #d6eefe 100%);
  }

  .lc-card {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 12px;
    border-radius: 16px;
    margin-top: -12px;
    background: #ffffff;
  }

  /*
    抽奖码行的容器：条数少时随内容自然高度（flex:1 在没有溢出时不会强行拉伸）。
    条数多到把 .lc-sheet-inner 顶到 80vh 上限时，min-height:0 让这一层
    实际收缩，超出部分变成 overflow-y:auto 的内部滚动，
    「查看更多/收起」和确认按钮因为是 flex-shrink:0 始终留在滚动区域外。
  */
  .lc-rows {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: 8px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* 滚动区域右边贴内容边缘会很挤，留一点余白给滚动条（虽然大部分移动端不显示） */
    padding-right: 2px;
  }

  /*
    单条抽奖码（设计稿）：宽 fill(319) / 高 66 / radius 16
    / padding 8px 12px / gap 8 / 底色 #E9F2FD
    / inner shadow 0 0 7px #58A8FF 30%
  */
  .lc-row {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 66px;
    padding: 8px 12px;
    border-radius: 16px;
    background: #e9f2fd;
    box-shadow: inset 0 0 7px rgba(88, 168, 255, 0.3);
  }

  .lc-row-main {
    flex: 1;
    min-width: 0;
  }

  .lc-no {
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    color: #1d1d1d;
    word-break: break-all;
  }

  .lc-meta {
    margin-top: 2px;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: rgba(29, 29, 29, 0.6);
  }

  .lc-row-status {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;
  }

  /*
    状态标签（设计稿）：宽度 hug / 高 32 / radius 54
    / padding 3px 16px / 14px 400
  */
  .lc-tag {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 32px;
    padding: 3px 16px;
    border-radius: 54px;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0;
    white-space: nowrap;
  }

  .lc-tag-icon {
    width: 14px;
    height: 14px;
  }

  /* 待开始 / 待开奖（设计稿）：#1D1D1D 60% 字，底 #1D1D1D1A */
  .lc-tag.is-pending {
    background: rgba(29, 29, 29, 0.1);
    color: rgba(29, 29, 29, 0.6);
  }

  /*
    已中奖（设计稿）：hug(91) / 高 24 / padding 3px 8px
    / #00A876 字，底 #00A876 10%
  */
  .lc-tag.is-won {
    height: 24px;
    padding: 3px 8px;
    background: rgba(0, 168, 118, 0.1);
    color: #00a876;
  }

  /*
    中奖等级（设计稿）：hug(74) / 高 24 / padding 3px 16px
    / 14px 400 / #FF6140 字，底 #FF6140 10%
  */
  .lc-tag.is-level {
    height: 24px;
    background: rgba(255, 97, 64, 0.1);
    color: #ff6140;
  }

  /* 未中奖（设计稿）：14px / lh 22px / #FC3F5C 字，底 #FC3F5C33 */
  .lc-tag.is-lost {
    background: rgba(252, 63, 92, 0.2);
    line-height: 22px;
    color: #fc3f5c;
  }

  /* 查看更多（设计稿）：14px / 600 / line-height 24px / #5BA7FF */
  /* 空态 */
  .lc-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
  }

  .lc-empty-icon {
    width: 80px;
    height: auto;
  }

  .lc-empty-text {
    margin-top: 8px;
    font-size: 14px;
    color: rgba(29, 29, 29, 0.6);
  }

  .lc-more {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    /* .lc-card 不再统一给 gap，这里补上原来跟 .lc-rows 之间的 8px 间距 */
    margin-top: 8px;
    padding: 10px 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0;
    color: #5ba7ff;
  }

  .lc-more-arrow {
    width: 14px;
    height: 14px;
    transition: transform 0.2s;
  }

  .lc-more-arrow.is-up {
    transform: rotate(180deg);
  }

  /* 确认按钮：纯色 #3B66F4 */
  .lc-confirm {
    flex-shrink: 0;
    width: 100%;
    height: 48px;
    margin-top: 16px;
    border-radius: 24px;
    background: #3b66f4;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    color: #ffffff;
  }
</style>
